import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, uploadFileToSignedUrl } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { Modal } from '../components/Modal.tsx';
import { CommentThread } from '../components/CommentThread.tsx';
import { DocumentTable } from '../components/DocumentTable.tsx';
import { ReplyComposer, type ReplyPayload } from '../components/ReplyComposer.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import type { DocVersionFile } from '../lib/documentVersions.ts';
import type { Question, SubmissionComment } from '../lib/types.ts';

// CommentModal renders the full admin↔vendor thread for a submission, the
// document version table for the question's files, and a reply composer that
// lets the admin attach one or more files (each tagged reference / replaces /
// new) alongside an optional message.
export function CommentModal({
  question,
  companyId,
  submissionId,
  onClose,
}: {
  question: Question;
  companyId: string;
  submissionId: string | undefined;
  onClose: () => void;
}) {
  const qc = useQueryClient();

  const commentsQuery = useQuery({
    queryKey: ['company', companyId, 'submissions', submissionId, 'comments'],
    queryFn: () =>
      adminApi.get<{ comments: SubmissionComment[] }>(
        `/api/companies/${companyId}/submissions/${submissionId}/comments`,
      ),
    enabled: !!submissionId,
    retry: 1,
  });

  const filesHistoryQuery = useQuery({
    queryKey: ['company', companyId, 'submission', submissionId, 'file-history'],
    queryFn: () =>
      adminApi.get<{ files: DocVersionFile[] }>(
        `/api/companies/${companyId}/submissions/${submissionId}/files/history`,
      ),
    enabled: !!submissionId,
    retry: 1,
  });

  const postMutation = useMutation({
    mutationFn: async (payload: ReplyPayload) => {
      const attachments = [];
      for (const a of payload.attachments) {
        const presigned = await adminApi.post<{ path: string; url: string }>(
          `/api/companies/${companyId}/submissions/${question.id}/upload-url`,
          { file_name: a.file.name, mime_type: a.file.type },
        );
        await uploadFileToSignedUrl(presigned.url, a.file);
        attachments.push({
          attachment_path: presigned.path,
          attachment_name: a.file.name,
          attachment_size_bytes: a.file.size,
          attachment_mime_type: a.file.type || 'application/octet-stream',
          attachment_expires_at: a.expiresAt ?? undefined,
          replaces_file_ids: a.replacesFileIds,
          mode: a.mode,
        });
      }
      return adminApi.post(`/api/companies/${companyId}/submissions/${submissionId}/comments`, {
        ...(payload.body ? { body: payload.body } : {}),
        ...(attachments.length ? { attachments } : {}),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['company', companyId, 'submissions', submissionId, 'comments'],
      });
      void qc.invalidateQueries({
        queryKey: ['company', companyId, 'submission', submissionId, 'file-history'],
      });
      void qc.invalidateQueries({ queryKey: ['company', companyId, 'submissions'] });
      void qc.invalidateQueries({ queryKey: ['company', companyId] });
      void qc.invalidateQueries({ queryKey: ['company', companyId, 'pending-changes'] });
    },
  });

  return (
    <Modal
      open
      title={`Comments — ${question.label}`}
      size="lg"
      onClose={onClose}
      footer={
        <Btn size="sm" variant="ghost" onClick={onClose}>
          Close
        </Btn>
      }
    >
      {commentsQuery.isError ? (
        <div className="space-y-2">
          <ErrorMessage error={commentsQuery.error} />
          <button
            type="button"
            className="text-xs text-[#4D148C] underline hover:text-[#3B0F6B]"
            onClick={() => void commentsQuery.refetch()}
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          {(filesHistoryQuery.data?.files.length ?? 0) > 0 && (
            <DocumentTable files={filesHistoryQuery.data!.files} />
          )}
          <CommentThread comments={commentsQuery.data?.comments ?? []} />
          {submissionId && (
            <ReplyComposer
              role="admin"
              isDocumentQuestion={question.type === 'document_upload'}
              allowMultipleFiles={question.allow_multiple_files ?? true}
              currentFiles={filesHistoryQuery.data?.files ?? []}
              onReply={(p) => postMutation.mutate(p)}
              pending={postMutation.isPending}
              error={postMutation.error as Error | null}
            />
          )}
        </div>
      )}
    </Modal>
  );
}
